import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import type { AppShellProps, NavItem } from './types';

export function railItemSx(collapsed: boolean, active: boolean): SystemStyleObject<Theme> {
  let activeAfter: SystemStyleObject<Theme> = {};
  if (active) {
    let edge: SystemStyleObject<Theme> = { left: 8, right: 8 };
    if (collapsed) {
      edge = { left: 0, right: 0 };
    }
    activeAfter = {
      content: '""',
      position: 'absolute',
      bottom: 0,
      height: '2px',
      bgcolor: 'primary.main',
      ...edge,
    };
  }
  let color = 'text.secondary';
  if (active) {
    color = 'primary.main';
  }
  return {
    position: 'relative',
    width: '100%',
    borderRadius: 1,
    display: 'flex',
    flexDirection: collapsed ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    textAlign: collapsed ? 'center' : 'left',
    columnGap: collapsed ? 0 : 1.25,
    px: collapsed ? 0.5 : 1.5,
    py: 1.25,
    color: color,
    '&:hover': { bgcolor: active ? 'transparent' : 'action.hover' },
    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: '-2px',
    },
    '&::after': activeAfter,
  };
}

/*
 * Sized absolutely rather than in rem. These are the same pixel sizes a 16px root gives, so
 * nothing moves in the SPAs — but the server-rendered pages declare a 14px root on wide screens,
 * and rem cannot be scoped to a subtree, so the identical component rendered 12.5% smaller there.
 * A component mounted into hosts it does not control cannot take its scale from their root.
 */
export function railLabelSx(collapsed: boolean, active: boolean) {
  const activeWeight = 400;
  return collapsed
    ? {
        fontSize: '10px',
        mt: 0.25,
        lineHeight: 1.2,
        width: '100%',
        fontWeight: active ? activeWeight : 400,
      }
    : {
        fontSize: '14px',
        flex: '1 1 0',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: active ? activeWeight : 400,
      };
}

type NavButtonProps = {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  linkComponent: AppShellProps['linkComponent'];
  emitDocAttributes: boolean;
};

export function NavButton({ item, active, collapsed, linkComponent, emitDocAttributes }: NavButtonProps) {
  const linkProps = item.spa ? { component: linkComponent, to: item.to } : { component: 'a' as const, href: item.to };
  const docProps = emitDocAttributes ? { 'data-doc-action': 'navigate', 'data-doc-context': item.match } : {};
  // MUI sets its own title while the tooltip is closed; leaving ours would show both on hover.
  let nativeTitle: string | undefined = item.label;
  if (item.tooltip !== undefined) {
    nativeTitle = undefined;
  }
  const button = (
    <ButtonBase
      {...linkProps}
      {...docProps}
      onClick={item.onClick}
      title={nativeTitle}
      aria-current={active ? 'page' : undefined}
      sx={railItemSx(collapsed, active)}
    >
      {item.icon}
      <Typography sx={railLabelSx(collapsed, active)}>{item.label}</Typography>
    </ButtonBase>
  );
  let rendered = button;
  if (item.tooltip !== undefined) {
    rendered = (
      <Tooltip title={item.tooltip} placement="right" describeChild>
        {button}
      </Tooltip>
    );
  }
  return rendered;
}
